import type { SVGProps } from "react"

// Inline-SVG icon set for the dark call widget. The v2 stack
// has no icon dependency, so chrome components hand-roll SVGs
// — we follow that convention. All icons inherit
// `currentColor` so callers control color via text classes.

// Design-matched glyphs for the dark call widget (CON-155).
// Drawn on a 16×16 grid with `currentColor`, so each control
// inverts cleanly with its button variant (cream/charcoal/
// brand/red). Width/height default to 16 but callers override.

// Four-point AI sparkle for the "AI is listening" pill.
export function SparkleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 1C8 4 9 7 12 7C15 7 8 8 8 8C8 8 15 9 12 9C9 9 8 12 8 15C8 12 7 9 4 9C1 9 8 8 8 8C8 8 1 7 4 7C7 7 8 4 8 1Z" />
    </svg>
  )
}

// Document-with-lines glyph for the Note control.
export function NoteDocIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M9 2.5H4.5C3.7 2.5 3 3.2 3 4V12C3 12.8 3.7 13.5 4.5 13.5H11C11.8 13.5 12.5 12.8 12.5 12V6L9 2.5Z" />
      <path d="M5.5 8.5H10M5.5 10.5H8.5" />
    </svg>
  )
}

// Finer-stroke mic for the dark widget's Mute control.
export function MicLineIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 2.5C7.1 2.5 6.4 3.2 6.4 4.1V8C6.4 8.9 7.1 9.6 8 9.6C8.9 9.6 9.6 8.9 9.6 8V4.1C9.6 3.2 8.9 2.5 8 2.5Z" />
      <path d="M4.5 7.5C4.5 9.4 6.1 11 8 11C9.9 11 11.5 9.4 11.5 7.5M8 11V13.5" />
    </svg>
  )
}

// Mic with a strike-through for the muted (Unmute) state.
export function MicLineOffIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 2.5C7.1 2.5 6.4 3.2 6.4 4.1V8C6.4 8.9 7.1 9.6 8 9.6C8.9 9.6 9.6 8.9 9.6 8V4.1C9.6 3.2 8.9 2.5 8 2.5Z" />
      <path d="M4.5 7.5C4.5 9.4 6.1 11 8 11C9.9 11 11.5 9.4 11.5 7.5M8 11V13.5" />
      <path d="M3 3L13 13" />
    </svg>
  )
}

// Filled 3×3 dot grid for the Keypad control.
export function DotGridIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      {[4, 8, 12].map((cy) =>
        [4, 8, 12].map((cx) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.1} />
        )),
      )}
    </svg>
  )
}

// Solid downward-tilted phone for hang-up / decline / end.
export function PhoneDownSolidIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M2.6 9.3C2.3 9 2.3 8.5 2.6 8.2C5.6 5.4 10.4 5.4 13.4 8.2C13.7 8.5 13.7 9 13.4 9.3L12.2 10.4C11.9 10.7 11.5 10.7 11.2 10.5L9.9 9.6C9.7 9.4 9.5 9.1 9.6 8.8L9.7 7.9C8.6 7.5 7.4 7.5 6.3 7.9L6.4 8.8C6.5 9.1 6.3 9.4 6.1 9.6L4.8 10.5C4.5 10.7 4.1 10.7 3.8 10.4L2.6 9.3Z" />
    </svg>
  )
}

// Solid handset for the Answer affordance.
export function PhoneSolidIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M5.5 3C5 3 4 3.5 3.7 4.4c-.6 1.8.4 4.3 2.4 6.3s4.5 3 6.3 2.4c.9-.3 1.4-1.3 1.4-1.8 0-.3-.2-.5-.4-.6l-2-.9c-.3-.1-.6 0-.8.2l-.6.7c-1-.5-2-1.5-2.5-2.5l.7-.6c.2-.2.3-.5.2-.8l-.9-2C7 3.2 6.8 3 6.5 3h-1Z" />
    </svg>
  )
}

// Outline info circle for the "couldn't connect" notice.
export function InfoCircleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 5V8.5" />
      <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  )
}

// Refresh arrow for "Try again".
export function RefreshIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3 8a5 5 0 1 1 1.5 3.5M3 8V5m0 3h3" />
    </svg>
  )
}

// Checkmark for the "Logged" pill, the disposition dropdown's
// current option, and the ticked follow-up checkbox.
export function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3.5 8.5L6.5 11.5L12.5 5" />
    </svg>
  )
}

// Three stacked lines — the pipeline indicator on the
// Link-a-deal create step ("Sales pipeline · Lead").
export function LinesIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3 3.5h10M3 7h10M3 10.5h6" />
    </svg>
  )
}

// A reset / undo loop for "Reset to AI summary".
export function ResetIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M9.5 3.5a5 5 0 1 0 2 4M9.5 3.5V1m0 2.5H7" />
    </svg>
  )
}

// A chat bubble for the no-answer "Message on WhatsApp" pivot.
export function ChatBubbleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 1.5C4.4 1.5 1.5 4.4 1.5 8c0 1.2.3 2.3.9 3.3L1.5 14.5l3.3-.9c1 .5 2 .8 3.2.8 3.6 0 6.5-2.9 6.5-6.5S11.6 1.5 8 1.5Z" />
    </svg>
  )
}

// A plus for the no-deal "Link a deal" affordance.
export function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 3.5V12.5M3.5 8h9" />
    </svg>
  )
}

// An × for closing the More sheet.
export function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  )
}

// A trash can for "Delete this note".
export function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3 4.5h10M6 4.5V3h4v1.5M5 4.5l.6 8h4.8l.6-8" />
    </svg>
  )
}

// A small calendar for the Saved confirmation's follow-up line.
export function CalendarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      aria-hidden="true"
      {...props}
    >
      <rect x="2.5" y="3" width="11" height="10" rx="2" />
      <path d="M2.5 6H13.5" />
    </svg>
  )
}

// A speech note glyph for the Saved confirmation's note line.
export function NotePlacedIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3 3.5h10v7H6l-3 2.5z" />
    </svg>
  )
}

// A magnifier for the Link-a-deal picker's search field.
export function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx={7} cy={7} r={4.5} />
      <path d="M10.5 10.5L14 14" />
    </svg>
  )
}

// A right chevron marking a tappable deal row in the picker.
export function ChevronRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={8}
      height={14}
      viewBox="0 0 8 14"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M1.5 1L6.5 7L1.5 13" />
    </svg>
  )
}

// A briefcase for the Link-a-deal empty state ("No deals yet").
export function BriefcaseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x={3} y={6} width={14} height={10} rx={2} />
      <path d="M7.5 6V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 12.5 5v1" />
    </svg>
  )
}

// Two stacked sheets for "Copy transcript" in the call-detail
// panel footer (CON-197 C5).
export function CopyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x={5.5} y={5.5} width={8} height={8} rx={1.5} />
      <path d="M10.5 5.5V4A1.5 1.5 0 0 0 9 2.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" />
    </svg>
  )
}

// A tray-with-down-arrow for "Download" in the call-detail
// panel footer (CON-197 C5).
export function DownloadIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 2.5v7M5 6.5L8 9.5l3-3" />
      <path d="M3 11.5v1A1 1 0 0 0 4 13.5h8a1 1 0 0 0 1-1v-1" />
    </svg>
  )
}
